import React, { useState, useEffect } from 'react'

export default function LightboxGallery({ images = [], initialIndex = 0, isOpen, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  useEffect(() => {
    setCurrentIndex(initialIndex)
  }, [initialIndex, isOpen])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') handleNext()
      if (e.key === 'ArrowLeft') handlePrev()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, currentIndex, images.length])

  if (!isOpen || images.length === 0) return null

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  const currentImage = images[currentIndex]

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
      {/* Top Bar Controls */}
      <div className="absolute top-4 left-4 right-4 flex justify-between items-center text-white z-20">
        <span className="text-xs font-mono bg-black/50 px-3 py-1.5 rounded-full border border-white/20">
          {currentIndex + 1} / {images.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          aria-label="Luk galleri"
        >
          <i className="fa-solid fa-xmark text-xl" />
        </button>
      </div>

      {/* Main Image */}
      <div className="relative max-w-6xl max-h-[85vh] w-full flex items-center justify-center p-2">
        <img
          src={currentImage}
          alt={`Billede ${currentIndex + 1}`}
          className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl transition-all duration-300 select-none"
        />
      </div>

      {/* Navigation Buttons */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3.5 rounded-full bg-black/60 hover:bg-[#2BABE3] text-white hover:text-white transition-colors border border-white/20 z-20"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-lg" />
          </button>

          <button
            type="button"
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3.5 rounded-full bg-black/60 hover:bg-[#2BABE3] text-white hover:text-white transition-colors border border-white/20 z-20"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-lg" />
          </button>
        </>
      )}

      {/* Thumbnails Strip */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-4 right-4 flex justify-center gap-2 overflow-x-auto py-2 z-20">
          {images.map((img, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setCurrentIndex(idx)}
              className={`relative rounded-md overflow-hidden shrink-0 border-2 transition-all ${
                idx === currentIndex ? 'border-[#2BABE3] scale-105' : 'border-transparent opacity-50 hover:opacity-100'
              }`}
            >
              <img src={img} alt={`Miniature ${idx + 1}`} className="w-14 h-10 object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
